import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
} from "@/core/utils/func/money-minor";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";
import { useWalletSelectOptions } from "@/features/shared/api";
import {
  createEmoneyApprovalSchema,
  type EmoneyApprovalFormValues,
} from "@/features/wallet/schemas/emoney.schema";

const EMPTY: EmoneyApprovalFormValues = {
  walletId: "",
  amount: 0,
  bankReference: "",
  reason: "",
};

/**
 * Ouvre une demande `EMONEY_ISSUANCE` / `EMONEY_REDEMPTION` (maker).
 * L'émission réelle est exécutée par l'approbateur (quatre yeux).
 */
export function EmoneyApprovalForm({
  kind,
  defaultWalletId,
  t,
}: {
  kind: "issue" | "redeem";
  defaultWalletId?: string;
  t: (key: string) => string;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit.
  const schema = useMemo(() => createEmoneyApprovalSchema(), [i18n.language]);
  const submitApproval = useSubmitApprovalRequest();
  // Issue / redeem n'opèrent que sur les wallets PLATFORM (guide §5.5) —
  // on scope le select pour éviter de proposer un wallet INDIVIDUAL/MERCHANT.
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(true, { ownerType: "PLATFORM" });
  const [preview, setPreview] = useState<EmoneyApprovalFormValues | null>(null);

  const actionType = kind === "issue" ? "EMONEY_ISSUANCE" : "EMONEY_REDEMPTION";

  const form = useForm({
    defaultValues: {
      ...EMPTY,
      walletId: defaultWalletId ?? "",
    },
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(parsed.data);
    },
  });

  const fieldError =
    (name: keyof EmoneyApprovalFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const confirm = () => {
    if (!preview) return;
    const amountMinor = majorToMinor(preview.amount, DEFAULT_CURRENCY);
    submitApproval.mutate(
      {
        actionType,
        sourceService: "wallet-service",
        resourceType: "Wallet",
        resourceId: preview.walletId,
        payload: {
          amountMinor,
          currency: DEFAULT_CURRENCY,
          bankReference: preview.bankReference,
        },
        reason: preview.reason,
      },
      {
        onSuccess: () => {
          notifSilence(t("wallet.emoney.feedback.approvalRequested"));
          setPreview(null);
          navigate("/app/admin/approvals");
        },
        onError: (error) =>
          notifSilenceError(apiErrorHandler(error)),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {kind === "issue"
            ? t("wallet.emoney.form.submitIssue")
            : t("wallet.emoney.form.submitRedeem")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="walletId" validators={{ onChange: fieldError("walletId") }}>
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("wallet.emoney.form.walletId")}</Label>
                <EntitySearchableSelect
                  value={field.state.value}
                  onValueChange={field.handleChange}
                  options={walletOptions}
                  isLoading={walletsLoading}
                  placeholder={t("wallet.emoney.form.walletIdPlaceholder")}
                />
                {field.state.meta.errors[0] ? (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                ) : null}
              </div>
            )}
          </form.Field>
          <form.Field name="amount" validators={{ onChange: fieldError("amount") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor={`emoney-amount-${kind}`}>
                  {t("wallet.emoney.form.amount")}
                </Label>
                <Input
                  id={`emoney-amount-${kind}`}
                  type="number"
                  min={1}
                  placeholder={t("wallet.emoney.form.amountPlaceholder")}
                  value={field.state.value || ""}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? 0 : Number(e.target.value)
                    )
                  }
                />
                {field.state.meta.errors[0] ? (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                ) : null}
              </div>
            )}
          </form.Field>
          <form.Field
            name="bankReference"
            validators={{ onChange: fieldError("bankReference") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor={`emoney-bank-${kind}`}>
                  {t("wallet.emoney.form.bankReference")}
                </Label>
                <Input
                  id={`emoney-bank-${kind}`}
                  placeholder={t("wallet.emoney.form.bankReferencePlaceholder")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors[0] ? (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                ) : null}
              </div>
            )}
          </form.Field>
          <form.Field name="reason" validators={{ onChange: fieldError("reason") }}>
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor={`emoney-reason-${kind}`}>
                  {t("wallet.emoney.form.reason")}
                </Label>
                <Textarea
                  id={`emoney-reason-${kind}`}
                  placeholder={t("wallet.emoney.form.reasonPlaceholder")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors[0] ? (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                ) : null}
              </div>
            )}
          </form.Field>
          <div className="sm:col-span-2">
            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <ButtonSpinner /> : null}
                  {kind === "issue"
                    ? t("wallet.emoney.form.submitIssue")
                    : t("wallet.emoney.form.submitRedeem")}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </CardContent>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={
          preview
            ? {
                actionType,
                walletId: preview.walletId,
                amountMinor: majorToMinor(preview.amount, DEFAULT_CURRENCY),
                currency: DEFAULT_CURRENCY,
                bankReference: preview.bankReference,
                reason: preview.reason,
              }
            : {}
        }
        title={t("wallet.emoney.preview.title")}
        description={t("wallet.emoney.preview.description")}
        t={t}
        pending={submitApproval.isPending}
        onConfirm={confirm}
      />
    </Card>
  );
}
